import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
  SelectChangeEvent,
  OutlinedInput,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PhotoIcon from "@mui/icons-material/Photo";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PaletteIcon from "@mui/icons-material/Palette";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CustomTable from "../../../components/Admin/Common/Table";
import CustomModal from "../../../components/Admin/Common/Modal";
import Action from "../../../components/Admin/Common/Action";
import type { ResourceSection as Section } from "../../../types/resourceSection";
import { resourceService } from "../../../services/api/resourcesService";
import { toast } from "react-toastify";
import ViewSectionModal from "../../../components/Admin/Sections/ViewSectionModal";
import { useAuth } from "../../../contexts/AuthContext";
import MediaUpload from '../../../components/Admin/MediaUpload';
import MediaSelector from '../../../components/Admin/MediaSelector';
import BulletPointsInput from '../../../components/Admin/Common/BulletPointsInput';

// Predefined background colors and accent colors for subsections
const BACKGROUND_COLORS = [
  { value: "", label: "None" },
  { value: "#f5f7fa", label: "Light Gray" },
  { value: "#e8f4fd", label: "Light Blue" },
  { value: "#f0f9f0", label: "Light Green" },
  { value: "#fdf8e8", label: "Cream" },
  { value: "#f9ebf0", label: "Light Pink" },
];

const ACCENT_COLORS = [
  { value: "#2196f3", label: "Blue" },
  { value: "#4caf50", label: "Green" },
  { value: "#f44336", label: "Red" },
  { value: "#ff9800", label: "Orange" },
  { value: "#9c27b0", label: "Purple" },
  { value: "#009688", label: "Teal" },
];

const BACKGROUND_STYLES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Gray" },
  { value: "gradient", label: "Gradient" },
  { value: "image", label: "Image" },
];

// For bulk-add new sections using the new structure
interface NewResourceSectionForm {
  mainTitle: string;
  mainDescription: string;
  subsections: {
    subtitle: string;
    description: string;
    bulletPoints: string[];
    imageUrl?: string;
    imageAlt?: string;
    videoUrl?: string;
    backgroundColor?: string;
    accentColor?: string;
  }[];
  coverImageUrl?: string;
  coverImageAlt?: string;
  backgroundStyle?: 'light' | 'dark' | 'gradient' | 'image';
  tags?: string[];
  featured?: boolean;
}

// Sample test data for quick resource creation
const SAMPLE_HOSPICE_RESOURCE = {
  mainTitle: "Understanding Hospice Care: A Guide to Compassionate End-of-Life Support",
  mainDescription: "Hospice care is a specialized approach focused on providing comfort and dignity during the final stages of life. This supportive care system addresses physical, emotional, and spiritual needs, ensuring that patients and their families receive compassionate care during this critical time.",
  subsections: [
    {
      subtitle: "Compassionate Patient-Centered Care",
      description: "Hospice care emphasizes individualized attention that honors each patient's values and personal wishes. This approach:",
      bulletPoints: [
        "Delivers tailored care plans addressing unique comfort requirements",
        "Focuses on alleviating pain and managing distressing symptoms",
        "Respects patient autonomy and individual care preferences",
        "Provides emotional and spiritual support to both patients and families",
        "Cultivates an environment of empathy, dignity, and respect"
      ],
      backgroundColor: "#f5f7fa",
      accentColor: "#2196f3"
    },
    {
      subtitle: "Integrated Multidisciplinary Approach",
      description: "Hospice care brings together a diverse team of professionals who work collaboratively to provide comprehensive support. This team-based strategy includes:",
      bulletPoints: [
        "Physicians who specialize in end-of-life care and symptom management",
        "Nurses with expertise in palliative and hospice practices",
        "Social workers who assist with emotional, practical, and logistical challenges",
        "Spiritual care advisors who offer guidance and solace",
        "Volunteers and complementary care providers to enhance overall well-being",
        "Regular team consultations to ensure coordinated, effective care delivery"
      ],
      backgroundColor: "#f0f9f0",
      accentColor: "#4caf50"
    },
    {
      subtitle: "Comprehensive Support for Life's Final Stages",
      description: "The hospice care approach addresses multiple aspects of the end-of-life journey:",
      bulletPoints: [
        "Proactive management of pain, discomfort, and other distressing symptoms",
        "Counseling services to support emotional and psychological well-being",
        "Guidance on advance care planning to ensure care aligns with patient wishes",
        "Assistance in creating a meaningful legacy and fulfilling final wishes",
        "Ongoing evaluation and adaptation of care plans to meet evolving needs",
        "Educational resources to empower families and caregivers in decision-making"
      ],
      backgroundColor: "#fdf8e8",
      accentColor: "#ff9800"
    }
  ],
  backgroundStyle: "light" as "light" | "dark" | "gradient" | "image",
  tags: ["Hospice", "End-of-Life Care", "Palliative Care", "Patient Support"],
  featured: true
};

const AdminSections: React.FC = () => {
  const theme = useTheme();
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);

  // State for the create/edit modal for existing records
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);

  // Updated formData now supports the new structure with media
  const [formData, setFormData] = useState<Partial<Section>>({
    mainTitle: "",
    mainDescription: "",
    subsections: [],
    coverImageUrl: "",
    coverImageAlt: "",
    backgroundStyle: "light",
    tags: [],
    featured: false,
  });

  // State for the "view" modal
  const [isViewOpen, setViewOpen] = useState(false);
  const [viewSection, setViewSection] = useState<Section | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for tags input
  const [tagInput, setTagInput] = useState("");

  // State for bulk-adding new sections using the new structure
  const [newSections, setNewSections] = useState<NewResourceSectionForm[]>([
    { 
      mainTitle: "", 
      mainDescription: "", 
      subsections: [],
      backgroundStyle: "light",
      featured: false,
    },
  ]);

  const { tokens } = useAuth();
  const token = tokens?.idToken;

  useEffect(() => {
    const loadSections = async () => {
      try {
        const data = await resourceService.getResources();
        setSections(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        toast.error("Failed to load sections.");
      } finally {
        setLoading(false);
      }
    };
    loadSections();
  }, []);

  const openModal = (mode: "create" | "edit", section?: Section) => {
    setModalMode(mode);
    if (mode === "edit" && section) {
      setSelectedSection(section);
      setFormData({
        mainTitle: section.mainTitle || "",
        mainDescription: section.mainDescription || "",
        subsections: Array.isArray(section.subsections) ? [...section.subsections] : [],
        coverImageUrl: section.coverImageUrl || "",
        coverImageAlt: section.coverImageAlt || "",
        backgroundStyle: section.backgroundStyle || "light",
        tags: section.tags || [],
        featured: section.featured || false,
      });
    } else {
      setSelectedSection(null);
      setFormData({
        mainTitle: "",
        mainDescription: "",
        subsections: [{
          subtitle: "",
          description: "",
          bulletPoints: [],
        }],
        coverImageUrl: "",
        coverImageAlt: "",
        backgroundStyle: "light",
        tags: [],
        featured: false,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedSection(null);
  };

  const openViewModal = (section: Section) => {
    setViewSection(section);
    setViewOpen(true);
  };

  const closeViewModal = () => {
    setViewOpen(false);
    setViewSection(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTagsChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags?.includes(tagInput.trim())) {
        setFormData({
          ...formData,
          tags: [...(formData.tags || []), tagInput.trim()],
        });
      }
      setTagInput("");
    }
  };

  const handleTagDelete = (tagToDelete: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(tag => tag !== tagToDelete),
    });
  };

  const handleSubsectionChange = (
    index: number,
    field: string,
    value: string | string[]
  ) => {
    setFormData((prev) => {
      const subsections = Array.isArray(prev.subsections)
        ? [...prev.subsections]
        : [];
      subsections[index] = {
        ...subsections[index],
        [field]: value,
      };
      return { ...prev, subsections };
    });
  };

  const addSubsection = () => {
    setFormData((prev) => {
      const subsections = Array.isArray(prev.subsections)
        ? [...prev.subsections]
        : [];
      subsections.push({
        subtitle: "",
        description: "",
        bulletPoints: [],
      });
      return { ...prev, subsections };
    });
  };

  const removeSubsection = (index: number) => {
    setFormData((prev) => {
      const subsections = Array.isArray(prev.subsections) ? [...prev.subsections] : [];
      subsections.splice(index, 1);
      return { ...prev, subsections };
    });
  };

  const handleFormSubmit = async () => {
    try {
      if (modalMode === "create") {
        if (!token) throw new Error("Authentication token missing");
        const newSection = await resourceService.createResource(formData, token);
        setSections((prev) => [...prev, newSection]);
        toast.success("Section created successfully!");
      } else if (modalMode === "edit" && selectedSection) {
        if (!token) throw new Error("Authentication token missing");
        const updated = await resourceService.updateResource(
          selectedSection.id,
          formData,
          token
        );
        setSections((prev) =>
          prev.map((sec) => (sec.id === selectedSection.id ? updated : sec))
        );
        toast.success("Section updated successfully!");
      }
      closeModal();
    } catch (error: any) {
      console.error("Error saving section:", error);
      toast.error(error.message || "Error saving section. Please try again.");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      if (!token) throw new Error("Authentication token missing");
      await resourceService.deleteResource(id, token);
      setSections((prev) => prev.filter((sec) => sec.id !== id));
      toast.success("Section deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting section:", error);
      toast.error(error.message || "Error deleting section. Please try again.");
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Add this function to the component
  const populateWithSampleData = () => {
    setFormData({
      mainTitle: SAMPLE_HOSPICE_RESOURCE.mainTitle,
      mainDescription: SAMPLE_HOSPICE_RESOURCE.mainDescription,
      subsections: SAMPLE_HOSPICE_RESOURCE.subsections,
      backgroundStyle: SAMPLE_HOSPICE_RESOURCE.backgroundStyle,
      tags: SAMPLE_HOSPICE_RESOURCE.tags,
      featured: SAMPLE_HOSPICE_RESOURCE.featured,
      coverImageUrl: "",
      coverImageAlt: "Hospice Care Guide"
    });
    toast.success("Sample data populated. Add images and videos as needed.");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Admin - Manage Resource Sections
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => openModal("create")}
        sx={{ mb: 2 }}
      >
        Add Resource Section
      </Button>
      <CustomTable<Section>
        columns={[
          { label: "Main Title", key: "mainTitle" },
          {
            label: "Subsections",
            key: "subsections",
            format: (subsections: any[]) => (subsections ? subsections.length : 0),
          },
          {
            label: "Featured",
            key: "featured",
            format: (featured: boolean) => featured ? "Yes" : "No",
          },
          {
            label: "Media",
            key: "coverImageUrl",
            format: (url: string) => url ? "Yes" : "No",
          },
        ]}
        data={sections}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        rowActions={(section) => (
          <Action
            actions={[
              { label: "View", onClick: () => openViewModal(section) },
              { label: "Edit", onClick: () => openModal("edit", section) },
              { label: "Delete", onClick: () => handleDelete(section.id) },
            ]}
          />
        )}
      />

      {/* Modal for editing/creating a single resource section */}
      <CustomModal
        title={`${modalMode === "create" ? "Create" : "Edit"} Resource Section`}
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleFormSubmit}
      >
        <Box component="form" noValidate autoComplete="off">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {modalMode === "create" && (
              <Button
                startIcon={<AutoFixHighIcon />}
                variant="outlined"
                color="secondary"
                onClick={populateWithSampleData}
                size="small"
              >
                Populate Sample Hospice Data
              </Button>
            )}
          </Box>
          
          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              General Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Main Title"
                  name="mainTitle"
                  value={formData.mainTitle || ""}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Main Description"
                  name="mainDescription"
                  value={formData.mainDescription || ""}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Visual Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Background Style</InputLabel>
                  <Select
                    name="backgroundStyle"
                    value={formData.backgroundStyle || "light"}
                    onChange={handleSelectChange}
                    label="Background Style"
                  >
                    {BACKGROUND_STYLES.map((style) => (
                      <MenuItem key={style.value} value={style.value}>
                        {style.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured || false}
                      onChange={handleSwitchChange}
                      name="featured"
                      color="primary"
                    />
                  }
                  label="Featured Resource"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cover Image URL"
                  name="coverImageUrl"
                  value={formData.coverImageUrl || ""}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: <PhotoIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cover Image Alt Text"
                  name="coverImageAlt"
                  value={formData.coverImageAlt || ""}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Descriptive text for accessibility"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Add Tags (Press Enter to add)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagsChange}
                  fullWidth
                  placeholder="healthcare, resources, etc."
                />
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {formData.tags?.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleTagDelete(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Preview of cover image if provided */}
              {formData.coverImageUrl && (
                <Grid item xs={12}>
                  <Box mt={1} sx={{ position: 'relative' }}>
                    <img 
                      src={formData.coverImageUrl} 
                      alt={formData.coverImageAlt || "Preview"} 
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }} 
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Media
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MediaSelector 
                  mediaType="image"
                  label="Cover Image" 
                  initialMediaUrl={formData.coverImageUrl || ''}
                  initialAltText={formData.coverImageAlt || ''}
                  onSelect={(url, altText) => {
                    handleInputChange({
                      target: { name: 'coverImageUrl', value: url }
                    } as React.ChangeEvent<HTMLInputElement>);
                    handleInputChange({
                      target: { name: 'coverImageAlt', value: altText || '' }
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Subsections
          </Typography>
          {formData.subsections && formData.subsections.map((sub, index) => (
            <Paper 
              key={index}
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                bgcolor: '#f9f9f9',
                borderRadius: 2,
                position: "relative",
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }}
            >
              {formData.subsections!.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => removeSubsection(index)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Subsection {index + 1} Content
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Subtitle"
                    value={sub.subtitle || ""}
                    onChange={(e) => handleSubsectionChange(index, "subtitle", e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={sub.description || ""}
                    onChange={(e) => handleSubsectionChange(index, "description", e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Media & Styling
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <MediaSelector 
                    mediaType="image"
                    label="Image" 
                    initialMediaUrl={sub.imageUrl || ''}
                    initialAltText={sub.imageAlt || ''}
                    onSelect={(url, altText) => {
                      handleSubsectionChange(index, "imageUrl", url);
                      handleSubsectionChange(index, "imageAlt", altText || '');
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MediaSelector 
                    mediaType="video"
                    label="Video" 
                    initialMediaUrl={sub.videoUrl || ''}
                    onSelect={(url) => {
                      handleSubsectionChange(index, "videoUrl", url);
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Background Color</InputLabel>
                    <Select
                      value={sub.backgroundColor || ""}
                      onChange={(e) => handleSubsectionChange(index, "backgroundColor", e.target.value)}
                      label="Background Color"
                    >
                      {BACKGROUND_COLORS.map((color) => (
                        <MenuItem key={color.value} value={color.value}>
                          <Box display="flex" alignItems="center">
                            {color.value && (
                              <Box 
                                sx={{ 
                                  width: 16, 
                                  height: 16, 
                                  borderRadius: '50%', 
                                  bgcolor: color.value,
                                  mr: 1,
                                  border: '1px solid #ddd'
                                }} 
                              />
                            )}
                            {color.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Accent Color</InputLabel>
                    <Select
                      value={sub.accentColor || ""}
                      onChange={(e) => handleSubsectionChange(index, "accentColor", e.target.value)}
                      label="Accent Color"
                    >
                      {ACCENT_COLORS.map((color) => (
                        <MenuItem key={color.value} value={color.value}>
                          <Box display="flex" alignItems="center">
                            <Box 
                              sx={{ 
                                width: 16, 
                                height: 16, 
                                borderRadius: '50%', 
                                bgcolor: color.value,
                                mr: 1
                              }} 
                            />
                            {color.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Bullet Points
                  </Typography>
                  <BulletPointsInput
                    bulletPoints={Array.isArray(sub.bulletPoints) ? sub.bulletPoints : []}
                    onChange={(bulletPoints) =>
                      handleSubsectionChange(index, "bulletPoints", bulletPoints)
                    }
                  />
                </Grid>
                
                {/* Preview section if image or video is added */}
                {(sub.imageUrl || sub.videoUrl) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Media Preview
                    </Typography>
                    <Box sx={{ 
                      border: '1px dashed #ccc', 
                      borderRadius: '8px', 
                      p: 2,
                      backgroundColor: sub.backgroundColor || '#fff',
                      maxHeight: '200px',
                      overflow: 'hidden'
                    }}>
                      {sub.imageUrl && (
                        <img 
                          src={sub.imageUrl} 
                          alt={sub.imageAlt || "Preview"} 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '180px',
                            objectFit: 'contain'
                          }} 
                        />
                      )}
                      {!sub.imageUrl && sub.videoUrl && (
                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '180px' }}>
                          <VideoLibraryIcon sx={{ fontSize: '3rem', opacity: 0.7 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            Video will display here on the public page
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}
          
          <Button 
            variant="outlined" 
            onClick={addSubsection}
            startIcon={<AddIcon />}
            sx={{ mb: 3 }}
          >
            Add Subsection
          </Button>
        </Box>
      </CustomModal>

      <ViewSectionModal open={isViewOpen} onClose={closeViewModal} section={viewSection} />

      <Divider sx={{ my: 4 }} />
    </Box>
  );
};

export default AdminSections;
